import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useDeepCompare, useDeepMemo, useDidMount } from '../../../hooks/useCompare';
import {
  FileFormat,
  OnExport,
  OnExportSchedule,
  OnScheduleExportDelete,
  QuickReportOrigin
} from '../DataTableAdvancedElements/DataTableQuickReports/utils/QuickReports.type';
import { DEFAULT_EXPORT_FILE_FORMATS } from '../DataTableAdvancedElements/DataTableQuickReports/utils/QuickReports.constants';
import { usePropsForwarderContext } from '../plugins/impl/context/ForwarderContext';
import { OnQuickReportsChanged, QueryModel, SortModel } from './DataTableQueryProvider/DataTableQuery.types';
import { useDataTableInitialSearchModelObserver } from './DataTableQueryProvider/DataTableInitialSearchModelProvider';
import { useDataTableQueryContext, useDataTableQueryObserver } from './DataTableQueryProvider/DataTableQueryProvider';
import { DATA_TABLE_LIMIT } from './getDataTableCount';
import { areQueryVariablesEqual } from './DataTableQueryProvider/params/queryVariablesComparator';
import { useDataTableFilterContext } from './DataTableFilterContext';

interface DataTableQuickReportsContextProps<Q extends QueryModel, S extends SortModel> {
  quickReports: OnQuickReportsChanged<Q, S>[] | undefined;
  setQuickReports: Dispatch<SetStateAction<OnQuickReportsChanged<Q, S>[] | undefined>>;
  activeQuickReportId: string | undefined;
  setActiveQuickReportId: (id?: string, quickReports?: OnQuickReportsChanged<Q, S>[] | undefined) => void;
  setQuickReportDrawer: Dispatch<SetStateAction<boolean | undefined>>;
  isQuickReportDrawerOpen: boolean | undefined;
  unSavedQuickReport: OnQuickReportsChanged<Q, S> | undefined;
  setUnSavedQuickReport: Dispatch<SetStateAction<OnQuickReportsChanged<Q, S> | undefined>>;
  onQuickReportClick: ((id: string, origin: QuickReportOrigin) => void) | undefined;
  setOnQuickReportClick: Dispatch<(id?: string, origin?: QuickReportOrigin) => void>;
  onExport: OnExport<Q, S> | undefined;
  setOnExport: Dispatch<SetStateAction<OnExport<Q, S> | undefined>>;
  exportFileFormats: FileFormat[];
  setExportFileFormats: Dispatch<SetStateAction<FileFormat[]>>;
  setOnExportSchedule: Dispatch<SetStateAction<OnExportSchedule<Q, S> | undefined>>;
  onExportSchedule: OnExportSchedule<Q, S> | undefined;
  onScheduleExportDelete: OnScheduleExportDelete<Q, S> | undefined;
  setOnScheduleExportDelete: Dispatch<SetStateAction<OnScheduleExportDelete<Q, S> | undefined>>;
  newQuickReportId: string | undefined;
  loadingQuickReportsDone: boolean | undefined;
  setNewQuickReportId: Dispatch<SetStateAction<string | undefined>>;
  activeQuickReport: OnQuickReportsChanged<Q, S> | null | undefined;
}

const DataTableQuickReportsContext = createContext<DataTableQuickReportsContextProps<any, any>>({
  quickReports: [],
  setQuickReports: () => {},
  activeQuickReportId: undefined,
  setActiveQuickReportId: () => {},
  setQuickReportDrawer: () => {},
  isQuickReportDrawerOpen: false,
  unSavedQuickReport: undefined,
  setUnSavedQuickReport: () => {},
  onQuickReportClick: () => {},
  setOnQuickReportClick: () => {},
  onExport: undefined,
  setOnExport: () => {},
  setOnExportSchedule: () => {},
  setOnScheduleExportDelete: () => {},
  onExportSchedule: undefined,
  exportFileFormats: DEFAULT_EXPORT_FILE_FORMATS,
  setExportFileFormats: () => {},
  onScheduleExportDelete: undefined,
  newQuickReportId: undefined,
  loadingQuickReportsDone: undefined,
  activeQuickReport: undefined,
  setNewQuickReportId: () => {}
});

interface DataTableQuickReportsProviderProps {
  loading?: boolean;
  dataTableId: string;
}

export const DataTableQuickReportsProvider = <Q extends QueryModel, S extends SortModel>({
  children,
  loading,
  dataTableId
}: PropsWithChildren<DataTableQuickReportsProviderProps>) => {
  const { getProps, isEnabled } = usePropsForwarderContext();
  const {
    onQuickReportsChanged,
    quickReports: initialQuickReports,
    loadingQuickReportsDone
  } = getProps('quickReports') ?? {};
  const hasQuickReports = isEnabled('quickReports');
  const [firstLoadingDone, setFirstLoadingDone] = useState<boolean>(!hasQuickReports);
  const [quickReports, setQuickReports] = useState<OnQuickReportsChanged<Q, S>[] | undefined>();
  const [activeQuickReportId, setActiveQuickReportId] = useState<string | undefined>();
  const [unSavedQuickReport, setUnSavedQuickReport] = useState<OnQuickReportsChanged<Q, S> | undefined>();
  const [isQuickReportDrawerOpen, setQuickReportDrawer] = useState<boolean | undefined>();
  const [exportFileFormats, setExportFileFormats] = useState<FileFormat[]>(DEFAULT_EXPORT_FILE_FORMATS);
  const [onExport, setOnExport] = useState<OnExport<Q, S> | undefined>();
  const [onExportSchedule, setOnExportSchedule] = useState<OnExportSchedule<Q, S> | undefined>();
  const [onScheduleExportDelete, setOnScheduleExportDelete] = useState<OnScheduleExportDelete<Q, S> | undefined>();
  const [newQuickReportId, setNewQuickReportId] = useState<string>();
  const [onQuickReportClick, setOnQuickReportClick] = useState<
    ((id?: string, origin?: QuickReportOrigin) => void) | undefined
  >();
  const { applyQueryVariables, queryVariables } = useDataTableQueryContext();
  const { setVisibleFilters } = useDataTableFilterContext();
  const activeQuickReport = useMemo(
    () => quickReports?.find((report) => report.quickReportId === activeQuickReportId) ?? null,
    [quickReports, activeQuickReportId]
  );

  const initialQuickReportsHasChanged = useDeepCompare(initialQuickReports) && firstLoadingDone && hasQuickReports;
  const quickReportsHasChanged = useDeepCompare(quickReports);

  useEffect(() => {
    if (!loading && !firstLoadingDone && hasQuickReports && initialQuickReports) {
      setQuickReports(initialQuickReports);
      setFirstLoadingDone(true);
    }
    if (initialQuickReportsHasChanged) {
      setQuickReports(initialQuickReports);
    }
  }, [loading, initialQuickReports, firstLoadingDone, hasQuickReports, initialQuickReportsHasChanged]);

  useDataTableInitialSearchModelObserver(dataTableId, ({ detail: model }) => {
    if (model?.quickReportId) {
      setActiveQuickReportId(model.quickReportId);
    }
  });

  useDataTableQueryObserver(dataTableId, (event) => {
    if (!hasQuickReports) {
      return;
    }
    if (!areQueryVariablesEqual(activeQuickReport, event.detail)) {
      // SetUnSaved Quick Report
      setUnSavedQuickReport(activeQuickReport ?? undefined);
    } else {
      setUnSavedQuickReport(undefined);
    }
  });

  // Check if it is a major changed. If the most important data has changed (query, sort)
  // This information can be useful for changing default Quick Reports (minor changed)
  const shouldNotifyUserAboutChange = useDeepCompare(
    quickReports?.map(({ query, sort, name }) => ({ query, sort, name }))
  );

  useDidMount(() => {
    // Don't update Quick Reports state if we have unSaved Reports
    if (quickReportsHasChanged && !unSavedQuickReport) {
      onQuickReportsChanged?.(quickReports ?? [], shouldNotifyUserAboutChange);
    }
    // exclude onQuickReportsChanged to avoid re-renders
  }, [quickReportsHasChanged, quickReports, unSavedQuickReport, shouldNotifyUserAboutChange]);

  const setActiveQuickReportIdHandler = useCallback(
    (id?: string, quickReportList = quickReports) => {
      setActiveQuickReportId(id);
      // If new Active Report was set, always reset unSaved Report
      const activeReport = quickReportList?.find((report) => report.quickReportId === id);
      if (activeReport) {
        applyQueryVariables(
          {
            quickReportId: activeReport.quickReportId,
            sort: activeReport.sort,
            query: activeReport.query,
            page: { limit: queryVariables?.page?.limit ?? DATA_TABLE_LIMIT, offset: 0 }
          },
          true
        );
        setVisibleFilters(Object.keys(activeReport.query ?? {}));
      } else {
        applyQueryVariables(
          {
            quickReportId: undefined,
            query: {
              search: queryVariables?.query?.search ?? ''
            },
            sort: undefined,
            page: { limit: queryVariables?.page?.limit ?? DATA_TABLE_LIMIT, offset: 0 }
          },
          true
        );
        setVisibleFilters([]);
      }
      setUnSavedQuickReport(undefined);
    },
    [applyQueryVariables, queryVariables?.page?.limit, queryVariables?.query?.search, quickReports, setVisibleFilters]
  );

  const contextValue = useDeepMemo(
    (): DataTableQuickReportsContextProps<Q, S> => ({
      quickReports,
      setQuickReports,
      activeQuickReportId,
      setActiveQuickReportId: setActiveQuickReportIdHandler,
      setQuickReportDrawer,
      isQuickReportDrawerOpen,
      unSavedQuickReport,
      setUnSavedQuickReport,
      setOnQuickReportClick,
      onQuickReportClick,
      setOnScheduleExportDelete,
      onScheduleExportDelete,
      setOnExportSchedule,
      onExportSchedule,
      setOnExport,
      onExport,
      setExportFileFormats,
      exportFileFormats,
      newQuickReportId,
      setNewQuickReportId,
      loadingQuickReportsDone,
      activeQuickReport
    }),
    [
      quickReports,
      activeQuickReportId,
      setActiveQuickReportIdHandler,
      isQuickReportDrawerOpen,
      unSavedQuickReport,
      onQuickReportClick,
      onScheduleExportDelete,
      onExportSchedule,
      onExport,
      exportFileFormats,
      newQuickReportId,
      loadingQuickReportsDone,
      activeQuickReport
    ]
  );
  return <DataTableQuickReportsContext.Provider value={contextValue}>{children}</DataTableQuickReportsContext.Provider>;
};

export const useDataTableQuickReportsContext = <
  Q extends QueryModel,
  S extends SortModel
>(): DataTableQuickReportsContextProps<Q, S> => {
  const context = useContext<DataTableQuickReportsContextProps<Q, S>>(DataTableQuickReportsContext);

  if (!context) {
    throw new Error('useDataTableQuickReportsContext must be used under DataTableQuickReportsContext');
  }
  return context ?? {};
};
